import { Player } from '@lottiefiles/react-lottie-player'
import moment from 'moment-timezone'
import { memo, useEffect, useMemo, useState } from 'react'
import { IoIosArrowBack, IoIosArrowForward, IoIosClose } from 'react-icons/io'
import { useShallow } from 'zustand/react/shallow'
import { useGetDates } from '../../../hooks/date/use-get-dates'
import { useTranslate } from '../../../hooks/language/use-translate'
import { useGetRocketGameLiveFirst } from '../../../hooks/queries/rockets-games-lives/use-get-rocket-game-live-first'
import { useGetRocketsGamesLives } from '../../../hooks/queries/rockets-games-lives/use-get-rockets-games-lives'
import { useGetRocketsGames } from '../../../hooks/queries/rockets-games/use-get-rockets-games'
import { useGlobalStore } from '../../../store/global-store'
import type { Gift } from '../../../types/lives/messages'

// O writer (serverless) grava date_contribute em `dayjs(...).tz(live.utc).format('YYYY-MM-DD')`.
// O modal precisa computar "hoje" no MESMO UTC do host — senão a query pega ontem.
function computeHostDay(liveUtc: string | undefined | null, fallback: string): string {
  if (!liveUtc) return fallback
  try {
    if (moment.tz.zone(liveUtc)) return moment.tz(liveUtc).format('YYYY-MM-DD')
    const byOffset = moment().utcOffset(liveUtc)
    if (byOffset.isValid()) return byOffset.format('YYYY-MM-DD')
  } catch {}
  return fallback
}

interface Item {
  id: number
  name: string
  imgItem: string
}

export const RocketGameModal = memo(() => {
  const liveUserId = useGlobalStore(useShallow((state) => state.liveUserId))
  const liveUtc = useGlobalStore(useShallow((state) => state.utc))

  const [currentRocketGameIndex, setCurrentRocketGameIndex] = useState(0)
  const [hasUserSelectedIndex, setHasUserSelectedIndex] = useState(false)

  const { rocketsGames } = useGetRocketsGames()
  const { translate } = useTranslate()
  const { day: deviceDay } = useGetDates()
  const day = computeHostDay(liveUtc, deviceDay)

  // Foguetes ativos do host hoje — usado pra abrir o modal no rocket atual.
  // Filtra pelo dia atual (date_contribute), igual ao fluxo da live, senão
  // pega registros de dias anteriores.
  const { rocketsGamesLives } = useGetRocketsGamesLives({
    typeFilterDateContribute: 'equal',
    dateContributeInitial: day,
    dateContributeFinal: day,
    userLiveId: liveUserId!,
  })

  // Encontra o rocket "ativo" agora (in_exploded > in_progress > await_exploded)
  // e posiciona o carrossel nele na 1ª render.
  useEffect(() => {
    if (hasUserSelectedIndex) return
    if (!rocketsGames?.length || !rocketsGamesLives?.length) return
    const priority: Record<string, number> = {
      inExploded: 0,
      inProgress: 1,
      awaitExploded: 2,
    }
    const active = rocketsGamesLives
      .filter(
        (r) =>
          r?.typeRocketGameProgress &&
          priority[r.typeRocketGameProgress as string] !== undefined,
      )
      .sort(
        (a, b) =>
          (priority[a.typeRocketGameProgress as string] ?? 99) -
          (priority[b.typeRocketGameProgress as string] ?? 99),
      )[0]
    if (!active) return
    const idx = rocketsGames.findIndex((g) => g?.id === active.rocketGameId)
    if (idx >= 0) setCurrentRocketGameIndex(idx)
  }, [rocketsGames, rocketsGamesLives, hasUserSelectedIndex])

  const currentRocketGame = rocketsGames?.[currentRocketGameIndex]

  const { rocketGameLive } = useGetRocketGameLiveFirst({
    rocketGameId: currentRocketGame?.id,
    userLiveId: liveUserId!,
    typeFilterDateContribute: 'equal',
    dateContributeInitial: day,
    dateContributeFinal: day,
  })

  const quantityDone = rocketGameLive?.quantityContribute ?? 0
  const totalQuantity = currentRocketGame?.quantityContribute ?? 0
  const progressType = rocketGameLive?.typeRocketGameProgress as string | undefined
  const isExploded = progressType === 'exploded'
  const isInExploded = progressType === 'inExploded'
  const isAwait = progressType === 'awaitExploded'
  const isInProgress = progressType === 'inProgress'
  const finished = isExploded || isInExploded
  const progressPct = finished
    ? 100
    : totalQuantity > 0
      ? Math.min(100, (quantityDone / totalQuantity) * 100)
      : 0
  const quantityLeft = Math.max(0, totalQuantity - quantityDone)

  // JSON do Lottie servido pela PROPRIA webview (public/games/rocket-game/).
  // O storage-audio NAO envia header CORS, entao fetch cross-origin do Lottie
  // era bloqueado no build de producao (o `/cdn` era proxy do Vite, so dev).
  // Mesma origem = sem CORS.
  const currentRocketAnimation = `/games/rocket-game/b${currentRocketGame?.level || 1}.json`

  const isFirst = currentRocketGameIndex <= 0
  const isLast = currentRocketGameIndex >= (rocketsGames?.length || 0) - 1

  function handlePrevRocketGame() {
    setHasUserSelectedIndex(true)
    setCurrentRocketGameIndex((prev) => Math.max(0, prev - 1))
  }

  function handleNextRocketGame() {
    setHasUserSelectedIndex(true)
    const max = (rocketsGames?.length || 0) - 1
    setCurrentRocketGameIndex((prev) => Math.min(max, prev + 1))
  }

  function handleClose() {
    if ('ReactNativeWebView' in window) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage('close')
    }
  }

  const { gifts, items } = useMemo(() => {
    const gifts: Gift[] = []
    const items: Item[] = []
    if (currentRocketGame?.rocketGameDetail?.length) {
      currentRocketGame.rocketGameDetail.forEach((detail) => {
        if (detail.giftId) gifts.push(detail.gift as Gift)
        if (detail.itemId) items.push(detail.item as Item)
      })
    }
    return { gifts, items }
  }, [currentRocketGame])

  // Estilo do status box conforme estado
  const statusStyle = (() => {
    if (isExploded) {
      return {
        bg: 'from-emerald-400/30 to-emerald-600/30 border-emerald-300/50',
        bar: 'from-emerald-300 to-emerald-500',
        label: translate('rocket-game-modal-this-bomb-already-exploded'),
        chip: 'bg-emerald-500/80',
      }
    }
    if (isInExploded) {
      return {
        bg: 'from-amber-400/30 to-orange-600/30 border-amber-300/50',
        bar: 'from-amber-300 to-orange-500',
        label: translate('rocket-game-modal-this-bomb-exploded'),
        chip: 'bg-orange-500/90',
      }
    }
    if (isAwait) {
      return {
        bg: 'from-sky-400/30 to-indigo-600/30 border-sky-300/50',
        bar: 'from-sky-300 to-indigo-500',
        label: 'Aguardando bomba anterior',
        chip: 'bg-sky-500/90',
      }
    }
    if (isInProgress) {
      return {
        bg: 'from-violet-400/30 to-fuchsia-600/30 border-violet-300/50',
        bar: 'from-fuchsia-300 to-violet-500',
        label: translate('rocket-game-modal-quantity-left', quantityLeft),
        chip: 'bg-violet-500/90',
      }
    }
    // Sem registro hoje pra esse nível: não trava com "estoure o anterior
    // primeiro" — só mostra os dados da bomba (quanto falta pra ela encher).
    return {
      bg: 'from-slate-400/30 to-slate-600/30 border-slate-300/50',
      bar: 'from-slate-300 to-slate-500',
      label: translate('rocket-game-modal-quantity-left', quantityLeft),
      chip: 'bg-slate-500/80',
    }
  })()

  return (
    <div className="relative min-h-screen w-screen overflow-y-auto text-white"
      style={{
        background:
          'radial-gradient(120% 80% at 50% 0%, #2d1b69 0%, #1a0f3d 45%, #0a0820 100%)',
      }}
    >
      {/* Estrelinhas sutis no fundo */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, white 0.5px, transparent 1.2px),' +
            'radial-gradient(circle at 70% 60%, white 0.6px, transparent 1.4px),' +
            'radial-gradient(circle at 40% 80%, white 0.4px, transparent 1px),' +
            'radial-gradient(circle at 85% 25%, white 0.5px, transparent 1.2px)',
          backgroundSize: '200px 200px, 300px 300px, 150px 150px, 250px 250px',
        }}
      />

      {/* Botão fechar */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-md transition hover:bg-white/25 active:scale-95"
        aria-label="close"
      >
        <IoIosClose className="h-6 w-6 text-white" />
      </button>


      <div className="relative z-10 mx-auto flex max-w-md flex-col items-center px-4 pb-8 pt-6">
        {/* Cabeçalho com nível */}
        <div className="mb-2 flex items-center">
          <span className={`rounded-full px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white ${statusStyle.chip}`}>
            Nível {(currentRocketGameIndex + 1)}/{rocketsGames?.length ?? '—'}
          </span>
        </div>

        {/* Carrossel: setas + foguete */}
        <div className="flex w-full items-center justify-between">
          <button
            onClick={handlePrevRocketGame}
            disabled={isFirst}
            className={`flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-md transition active:scale-95 ${isFirst ? 'opacity-30' : 'hover:bg-white/20'}`}
            aria-label="prev"
          >
            <IoIosArrowBack className="h-6 w-6 text-white" />
          </button>

          <div className="relative flex h-44 w-44 items-center justify-center">
            {/* glow */}
            <div
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(255,170,80,0.35) 0%, rgba(255,170,80,0) 70%)',
              }}
            />
            <Player
              loop
              autoplay
              src={currentRocketAnimation}
              style={{ width: 176, height: 176 }}
            />
          </div>

          <button
            onClick={handleNextRocketGame}
            disabled={isLast}
            className={`flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-md transition active:scale-95 ${isLast ? 'opacity-30' : 'hover:bg-white/20'}`}
            aria-label="next"
          >
            <IoIosArrowForward className="h-6 w-6 text-white" />
          </button>
        </div>

        {/* Card de status + barra de progresso */}
        <div
          className={`mt-4 w-full rounded-2xl border bg-gradient-to-br p-4 backdrop-blur-md ${statusStyle.bg}`}
        >
          <div className="flex items-center justify-between text-sm font-semibold">
            <span>{statusStyle.label}</span>
            {totalQuantity > 0 && (
              <span className="text-xs font-medium text-white/80">
                {finished
                  ? `${totalQuantity}/${totalQuantity}`
                  : `${quantityDone}/${totalQuantity}`}
              </span>
            )}
          </div>

          {(totalQuantity > 0 || finished) && (
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-black/30">
              <div
                className={`h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out ${statusStyle.bar}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
        </div>

        {/* Section: Ganhos de presentes */}
        {Boolean(gifts?.length) && (
          <section className="mt-4 w-full rounded-2xl border border-white/15 bg-white/5 p-3 backdrop-blur-md">
            <header className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold tracking-wide text-white/90">
                {translate('rocket-game-modal-gifts-earnings')}
              </h3>
              <span className="text-[11px] text-white/60">{gifts.length}</span>
            </header>
            <div className="grid grid-cols-4 gap-2">
              {gifts.map((gift) => (
                <PrizeCell
                  key={gift?.id}
                  src={gift?.imgGift}
                  name={gift?.name}
                />
              ))}
            </div>
          </section>
        )}

        {/* Section: Items ganhos */}
        {Boolean(items?.length) && (
          <section className="mt-3 w-full rounded-2xl border border-white/15 bg-white/5 p-3 backdrop-blur-md">
            <header className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold tracking-wide text-white/90">
                {translate('rocket-game-modal-itens-earnings')}
              </h3>
              <span className="text-[11px] text-white/60">{items.length}</span>
            </header>
            <div className="grid grid-cols-4 gap-2">
              {items.map((item) => (
                <PrizeCell
                  key={item?.id}
                  src={item?.imgItem}
                  name={item?.name}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
})

RocketGameModal.displayName = 'RocketGameModal'

const PrizeCell = memo(({ src, name }: { src?: string | null; name?: string | null }) => {
  const [errored, setErrored] = useState(false)
  const showImg = !!src && !errored
  return (
    <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-white/95 ring-1 ring-white/40 shadow-sm">
      {showImg ? (
        <img
          src={src!}
          alt={name ?? ''}
          className="h-[80%] w-[80%] object-contain"
          onError={() => setErrored(true)}
        />
      ) : (
        <span className="px-1 text-center text-[9px] font-medium leading-tight text-slate-700">
          {name ?? '?'}
        </span>
      )}
    </div>
  )
})

PrizeCell.displayName = 'PrizeCell'
